import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import leaderboardApi, { type LeaderboardEntry } from '../../services/leaderboardApi';
import './LeaderboardWidget.css';

interface LeaderboardWidgetProps {
  gameType?: 'overall' | 'kadi-ni-tidi' | 'teen-patti' | 'rummy';
  period?: 'daily' | 'weekly' | 'monthly' | 'all-time';
  limit?: number;
  showViewAll?: boolean;
}

const LeaderboardWidget: React.FC<LeaderboardWidgetProps> = ({
  gameType = 'overall',
  period = 'weekly',
  limit = 5,
  showViewAll = true
}) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, [gameType, period, limit]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leaderboardApi.getTopPlayers(gameType, period, limit);
      setEntries(data);
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error('Error loading leaderboard widget:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    navigate('/leaderboard');
  };

  const getRankIcon = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getStreakBadge = (streak: number): string | null => {
    if (streak >= 10) return '🔥';
    if (streak >= 5) return '⚡';
    if (streak >= 3) return '✨';
    return null;
  };

  const formatWinRate = (winRate: number): string => {
    return `${(winRate * 100).toFixed(0)}%`;
  };

  if (loading) {
    return (
      <div className="leaderboard-widget">
        <div className="widget-header">
          <h3 className="widget-title">🏆 Top Players</h3>
        </div>
        <div className="widget-loading">
          <div className="loading-spinner"></div>
          <p>Loading rankings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-widget">
        <div className="widget-header">
          <h3 className="widget-title">🏆 Top Players</h3>
        </div>
        <div className="widget-error">
          <p>{error}</p>
          <button onClick={loadLeaderboard} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-widget">
      <div className="widget-header">
        <h3 className="widget-title">🏆 Top Players</h3>
        <span className="widget-period">{period}</span>
      </div>
      
      {entries.length === 0 ? (
        <div className="widget-empty">
          <p>No rankings yet</p>
          <p className="empty-subtitle">Play games to appear on the leaderboard!</p>
        </div>
      ) : (
        <>
          <div className="widget-entries">
            {entries.map((entry, index) => {
              const rank = index + 1;
              const streakBadge = getStreakBadge(entry.currentWinStreak || 0);
              
              return (
                <div key={entry.userId._id} className={`widget-entry rank-${rank}`}>
                  <div className="entry-rank">{getRankIcon(rank)}</div>
                  
                  <div className="entry-player">
                    <div className="player-avatar">
                      {entry.userId?.avatar ? (
                        <img src={entry.userId.avatar} alt={entry.userId.username} />
                      ) : (
                        <div className="avatar-placeholder">
                          {entry.userId?.username.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="player-info">
                      <div className="player-name">
                        {entry.userId?.displayName || entry.userId?.username || 'Unknown Player'}
                        {streakBadge && <span className="streak-badge">{streakBadge}</span>}
                      </div>
                      <div className="player-stats-compact">
                        {entry.wins}W • {formatWinRate(entry.winRate || 0)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="entry-points">
                    {entry.points}
                    <span className="points-label">pts</span>
                  </div>
                </div>
              );
            })}
          </div>
          
          {showViewAll && (
            <div className="widget-footer">
              <button onClick={handleViewAll} className="view-all-btn">
                View Full Leaderboard →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeaderboardWidget;
